import { CommonService } from './../../../shared/services/common.service';
import { catchError, tap } from 'rxjs/operators';
import { AttachmentList, GmpProductDetail, InspectionFee, InspectionView, OffsiteFacility, Packaging } from './../models/Inspection';
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AccountService } from "../../../services/account.service";
import { environment } from "../../../../environments/environment";
import { DataTableResponse } from "../../../models/DatatableResponse";
import {
  ProductCategory,
  Inspection,
  InspectionTypes,
  InvestigationType,
  InspectionReason,
  ProductionLine
} from "../models/Inspection";
import { ODKResponse } from "../../../models/ApplicationModel";
import { SubDataVariables } from "../../../../environments/app.variables";
import { PaymentReciptDoc } from "../../../models/CommonModels";
import { Observable } from "rxjs";
import { FileUploadModel } from "../../../shared/components/file-upload.model";
import { ChecklistModel } from "../../../models/ChecklistModel";
import { DocumentRef } from '../../../models/DocumentRef';

@Injectable({
  providedIn: "root",
})
export class InspectionService {
  private docGeneratorUrl = environment.baseUrl + "/DocumentGenerator";
  private inspectionUrl = environment.baseUrl + "/Inspection";
  private preInspectionAppListUrl = this.inspectionUrl + "/GetPreInspectionApplications";
  private postLicensingInspUrl = this.inspectionUrl + "/PostLicensingInspection";
  private newLocalGMPInspectionUrl = this.inspectionUrl + "/LocalGMPInspection/New";
  private renewalLocalGMPInspectionUrl = this.inspectionUrl + "/LocalGMPInspection/Renewal";
  private investigationInspectionUrl = this.inspectionUrl + "/InvestigationInspection";
  private investigationTypeUrl = environment.baseUrl + "/InvestigationType";
  private inspectionReasonUrl = environment.baseUrl + "/InspectionReason";
  private informalMarketInspectionUrl = this.inspectionUrl + "/InformalMarketInspection";
  private newAbroadCgmpInspectionUrl = this.inspectionUrl + "/AbroadCGMPInspection/New";
  private newAbroadCgmpInspectionWaiverUrl = this.inspectionUrl + "/AbroadCGMPInspection/Waiver/New";
  private abroadCGMPRenewalInspectionUrl = this.inspectionUrl + "/AbroadCGMPInspection/Renewal";
  private abroadCGMPInspectionWaiverRenewalUrl = this.inspectionUrl + "/AbroadCGMPInspection/Waiver/Renewal";
  private abroadCGMPLineAdditionInspectionUrl = this.inspectionUrl + "/AbroadCGMPInspection/LineAddition";
  private paymentInfoUrl = this.inspectionUrl + "/SubmitPaymentInfo";
  private inspectionChecklistUrl = environment.baseUrl + "/ChecklistSpec/InspectionScreening";
  private _inspectionTypeCodes: Array<string> = [];
  private tobeQuarantinedAppFilter = false;
  private preInspectionAppStatusCode = null;
  private _legacyDataFilter = false;

  constructor(
    private http: HttpClient,
    private accountService: AccountService,
    private commonService:CommonService
  ) {
  }

  set inspectionTypeCodes(codes) {
    this._inspectionTypeCodes = codes;
  }

  set tobeQuarantinedFilter(filter){
    this.tobeQuarantinedAppFilter = filter;
  }

  set preInspectionAppStatus(status){
    this.preInspectionAppStatusCode = status;
  }

  set legacyDataFilter(filter){
    this._legacyDataFilter = filter;
  }

  getPreInspectionDataTableOptions(applications) {
    const that = this;
    let headers = new HttpHeaders();
    headers = headers.set("Content-Type", "application/x-www-form-urlencoded");
    headers = headers.set(
      "Authorization",
      "Bearer " + this.accountService.getTokenData()
    );

    let cols = [
      { data: "id" },
      { data: "applicationStatusCode" },
      { data: "facilityTypeName" },
      { data: "productType" },
      { data: "modifiedDate" },
      { data: "assignedUser" },
      { data: "dueDate" },
      { data: "ticketNo" },
    ];

    let options = {
      pagingType: "full_numbers",
      pageLength: 10,
      serverSide: true,
      processing: true,
      order: [[4, "desc"]],
      language: {
        processing:
          '<div class="sk-spinner sk-spinner-three-bounce"> <div class="sk-bounce1"></div> <div class="sk-bounce2"></div> <div class="sk-bounce3"></div> </div>',
      },
      ajax: (dataTablesParameters: any, callback) => {
        dataTablesParameters.tobeQuarantinedFilter = this.tobeQuarantinedAppFilter;
        dataTablesParameters.applicationStatusCode = this.preInspectionAppStatusCode;
        that.http.post<DataTableResponse>(this.preInspectionAppListUrl, $.param(dataTablesParameters),{ headers: headers }).subscribe(
            (resp) => {
              applications.splice(0, applications.length);
              // can't use users=resp.data because we want to pass by reference
              for (let app of resp.data) {
                applications.push(app);
              }
              callback({
                recordsTotal: resp.recordsTotal,
                recordsFiltered: resp.recordsFiltered,
                data: [],
              });
            },
            (error) => {
              callback({
                recordsTotal: 0,
                recordsFiltered: 0,
                data: [],
              });
            }
          );
      },
      columns: cols,
    };

    return options;
  }

  getPostLicensingInspCandidateFacilities(
    facilityTypeId: number,
    productTypeId: number
  ) {
    return this.http.get<any>(
      this.postLicensingInspUrl +
        "/GetFacilities/" +
        facilityTypeId +
        "/" +
        productTypeId
    );
  }

  getNewLocalGMPInspCandidateFacilities(productTypeId: number) {
    return this.http.get<any>(
      this.newLocalGMPInspectionUrl + "/GetFacilities/" + productTypeId
    );
  }

  getRenewalLocalGMPInspCandidateFacilities(productTypeId: number) {
    return this.http.get<any>(
      this.renewalLocalGMPInspectionUrl + "/GetFacilities/" + productTypeId
    );
  }

  getInvestigationInspCandidateFacilities(
    facilityTypeId: number,
    productTypeId: number
  ) {
    return this.http.get<any>(
      this.investigationInspectionUrl +
        "/GetFacilities/" +
        facilityTypeId +
        "/" +
        productTypeId
    );
  }

  getInformalMarketInspCandidateFacilities(
    facilityTypeId: number,
    productTypeId: number
  ) {
    return this.http.get<any>(
      this.informalMarketInspectionUrl +
        "/GetFacilities/" +
        facilityTypeId +
        "/" +
        productTypeId
    );
  }

  getAbroadCGMPRenewalCandidateFacilities() {
    return this.http.get<any>(
      this.abroadCGMPRenewalInspectionUrl + "/GetAbroadFacilities"
    );
  }

  getAbroadCGMPWaiverRenewalCandidateFacilities() {
    return this.http.get<any>(
      this.abroadCGMPInspectionWaiverRenewalUrl + "/GetAbroadFacilities"
    );
  }

  createInspection(inspection: Inspection, inspectionTypeCode: string) {
    if (inspectionTypeCode == InspectionTypes.POST_LICENSING_INSPECTION) {
      return this.http.post<any>(this.postLicensingInspUrl, inspection);
    } else if (inspectionTypeCode == InspectionTypes.NEW_LOCAL_GMP_INSPECTION) {
      return this.http.post<any>(this.newLocalGMPInspectionUrl, inspection);
    } else if (inspectionTypeCode == InspectionTypes.LOCAL_GMP_RENEWAL_INSPECTION) {
      return this.http.post<any>(this.renewalLocalGMPInspectionUrl, inspection);
    }  else if (inspectionTypeCode == InspectionTypes.INVESTIGATION_INSPECTION) {
      return this.http.post<any>(this.investigationInspectionUrl, inspection);
    } else if (
      inspectionTypeCode == InspectionTypes.INFORMAL_MARKET_INSPECTION
    ) {
      return this.http.post<any>(this.informalMarketInspectionUrl, inspection);
    } else if (
      inspectionTypeCode == InspectionTypes.NEW_ABROAD_CGMP_INSPECTION
    ) {
      return this.http.post<any>(this.newAbroadCgmpInspectionUrl, inspection);
    } else if (inspectionTypeCode == InspectionTypes.NEW_ABROAD_CGMP_INSPECTION_WAIVER) {
      return this.http.post<any>(
        this.newAbroadCgmpInspectionWaiverUrl,
        inspection
      );
    } else if (
      inspectionTypeCode == InspectionTypes.ABROAD_CGMP_RENEWAL_INSPECTION
    ) {
      return this.http.post<any>(
        this.abroadCGMPRenewalInspectionUrl,
        inspection
      );
    } else if (
      inspectionTypeCode ==
      InspectionTypes.ABROAD_CGMP_INSPECTION_WAIVER_RENEWAL
    ) {
      return this.http.post<any>(
        this.abroadCGMPInspectionWaiverRenewalUrl,
        inspection
      );
    } else if (
      inspectionTypeCode == InspectionTypes.ABROAD_CGMP_LINE_ADDITION_INSPECTION
    ) {
      return this.http.post<any>(
        this.abroadCGMPLineAdditionInspectionUrl,
        inspection
      );
    }
  }

  getInspectionListDataTable(inspections : InspectionView[], isAbroadCGMP : boolean, isLocalGMP : boolean) {
    const that = this;
    let headers = new HttpHeaders();
    headers = headers.set("Content-Type", "application/x-www-form-urlencoded");
    headers = headers.set(
      "Authorization",
      "Bearer " + this.accountService.getTokenData()
    );

    let cols = [];
    let isApplicant = this.accountService.isUserApplicant() || this.accountService.isUserRegistrant();
    if (isAbroadCGMP) {
      cols = [
        { data: "id" },
        { data: "inspectionStatusCode" },
        { data: "manufacturerName" },
        { data: "localAgentName" },
        { data: "manufacturerCountry"},
        { data: "siteAddress"},
        { data: "modifiedDate" },
        { data: "assignedUser", visible: !isApplicant },
        { data: "startDate", visible: !isApplicant },
        { data: "dueDate" , visible: !isApplicant},
      ];
    } else {
      if(isLocalGMP) {
        cols = [
          { data: "id" },
          { data: "inspectionStatusCode" },
          { data: "facilityName" },
          { data: "productType" },
          { data: "modifiedDate" },
          { data: "assignedUser" , visible: !isApplicant},
          { data: "startDate" , visible: !isApplicant},
          { data: "dueDate" , visible: !isApplicant},
        ];
      } else {
        cols = [
          { data: "id" },
          { data: "inspectionStatusCode" },
          { data: "facilityName" },
          { data: "productType" },
          { data: "modifiedDate" },
          { data: "assignedUser" , visible: !isApplicant },
          { data: "dueDate" , visible: !isApplicant},
        ];
      }

    }

    let options = {
      pagingType: "full_numbers",
      pageLength: 10,
      serverSide: true,
      processing: true,
      order: [[isAbroadCGMP ? 6: 4, "desc"]],
      language: {
        processing:
          '<div class="sk-spinner sk-spinner-three-bounce"> <div class="sk-bounce1"></div> <div class="sk-bounce2"></div> <div class="sk-bounce3"></div> </div>',
      },
      ajax: (dataTablesParameters: any, callback) => {
        let url = this.inspectionUrl + "/List";
        dataTablesParameters.inspectionTypeCodes = this._inspectionTypeCodes;
        dataTablesParameters.legacyDataFilter = this._legacyDataFilter;
        that.http
          .post<DataTableResponse>(url, $.param(dataTablesParameters), {
            headers: headers,
          })
          .subscribe(
            (resp) => {
              inspections.splice(0, inspections.length);
              // can't use users=resp.data because we want to pass by reference
              for (let app of resp.data) {
                inspections.push(app);
              }

              callback({
                recordsTotal: resp.recordsTotal,
                recordsFiltered: resp.recordsFiltered,
                data: [],
              });
            },
            (error) => {
              callback({
                recordsTotal: 0,
                recordsFiltered: 0,
                data: [],
              });
            }
          );
      },
      columns: cols,
    };

    return options;
  }

  getInspection(inspectionId) {
    return this.http.get<Inspection>(this.inspectionUrl + "/" + inspectionId);
  }

  generateInspectionQRCode(inspectionId) {
    return this.http.get<any>(
      this.docGeneratorUrl + "/GenerateInspectionQRCode/" + inspectionId
    );
  }

  changeInspectionStatus(data) {
    return this.http.post<any>(this.inspectionUrl + "/ChangeStatus", data);
  }

  getInspectionSubmittedResponse(inspectionResultId, isPreInspection) {
    if (isPreInspection) {
      return this.http.get<ODKResponse>(
        this.inspectionUrl +
          "/GetPreInspectionSubmittedResponse/" +
          inspectionResultId
      );
    } else {
      return this.http.get<ODKResponse>(
        this.inspectionUrl + "/GetSubmittedResponse/" + inspectionResultId
      );
    }
  }

  getAllUserUploadedDocRefs(inspectionTypeCode, categoryID, agencyID, applicantTypeID): Observable<any> {
      return this.http.get<any>(
        environment.baseUrl + `/DocumentRef/Inspection/${inspectionTypeCode}/${categoryID}/${agencyID}/${applicantTypeID}` 
      );
  }

  getTentativePlanDocumentRefs(isLocalGMP:boolean) : Observable<Array<DocumentRef>> {
    return this.http.get<Array<DocumentRef>>(environment.baseUrl + '/DocumentRef/Inspection/TentativePlanDocuments/' + isLocalGMP);
  }
  getInspectionReportDocumentRefs(isUpdate:boolean) : Observable<Array<DocumentRef>> {
    return this.http.get<Array<DocumentRef>>(environment.baseUrl + '/DocumentRef/Inspection/InspectionReportDocuments/' + isUpdate );
  }

  updateInspectorInspectionDocuments(payload: { inspectionID: number; documents: { id: number; referenceID: number }[] }) {
    return this.http.put<boolean>(this.inspectionUrl + '/UpdateInspectorInspectionDocuments', payload);
  }

   
    getGMPFinalDocumentRefs(isLocalGMP : boolean) : Observable<Array<DocumentRef>> {
    return this.http.get<Array<DocumentRef>>(environment.baseUrl + '/DocumentRef/Inspection/GMPFinalDocuments/' + isLocalGMP);
  }

  getGMPInspectionDocRefs(): Observable<Array<DocumentRef>> {
    return this.http.get<Array<DocumentRef>>(environment.baseUrl + '/DocumentRef/Inspection/GMPInspectionDocuments');
  }

  submitCapa(inspectionId) {
    return this.http.get<boolean>(
      this.inspectionUrl + "/SubmitCAPA/" + inspectionId
    );
  }
  submitCapaAndImplementation(inspectionId) {    return this.http.get<boolean>(
      this.inspectionUrl + "/SubmitCAPAAndImplementationEvidence/" + inspectionId
    );}


  submitImplementationReport(inspectionId) {
    return this.http.get<boolean>(
      this.inspectionUrl + "/SubmitImplementationReport/" + inspectionId
    );
  }
    submitTentativePlan(inspectionId) {
    return this.http.get<boolean>(
      this.inspectionUrl + "/SubmitTentativePlan/" + inspectionId
    );
  }

  getAttachmentByID(docId) {
    const attachmentUrl = SubDataVariables.INSPECTION_DOC_URL + "/" + docId;
    return this.http.get(attachmentUrl, { responseType: "blob" });
  }

  assignToInspection(data) {
    return this.http.post<boolean>(this.inspectionUrl + "/Assign", data);
  }

  getInvestigationTypes() {
    return this.http.get<InvestigationType[]>(this.investigationTypeUrl);
  }

  getInspectionReasons() {
    return this.http.get<InspectionReason[]>(this.inspectionReasonUrl);
  }

  uploadPaymentInfo(data: PaymentReciptDoc) {
    return this.http.post<any>(this.paymentInfoUrl, data);
  }

  getApplicantType(): Observable<any> {
    return this.http.get<any>(environment.baseUrl + "/ApplicantType");
  }

  getCountry(): Observable<any> {
    return this.http.get<any>(environment.baseUrl + "/Country");
  }

  getProductCategories(): Observable<any> {
    return this.http.get<any>(environment.baseUrl + "/ProductCategory");
  }

  getProductionLines(specificManuOpId: number): Observable<any> {
    return this.http.get<any>(
      environment.baseUrl + "/ProductCategory/GetProductionLines/" + specificManuOpId
    );
  }

  getManufacturingOps(productCategoryId: number): Observable<any> {
    return this.http.get<any>(environment.baseUrl + "/ProductCategory/GetManufacturingOperations/" + productCategoryId);
  }

  getSpecificManufacturingOps(manuOpId): Observable<any> {
    return this.http.get<any>(
      environment.baseUrl + "/ProductCategory/GetSpecificManufacturingOperations/" + manuOpId
    );
  }

  getPackagings(manuOpName): Observable<Packaging[]> {
    return this.http.get<Packaging[]>(environment.baseUrl + "/ProductCategory/GetPackagings/" + manuOpName);
  }

  getQualityControlTests(): Observable<any> {
    return this.http.get<any>(environment.baseUrl + "/QualityControlTest");
  }

  getDocuments(ispectionCodeType): Observable<any> {
    return this.http.get<any>(
      environment.baseUrl + "/DocumentRef/GMP/" + ispectionCodeType
    );
  }

  postAbroadApplicationForm(data,link) {
    return this.http.post<any>(environment.baseUrl + "/Inspection/AbroadCGMPInspection/"+link,data);
  }

  editAbroadApplicationForm(data,link){
    return this.http.put<any>(environment.baseUrl + "/Inspection/AbroadCGMPInspection/"+link,data);
  }

  postLocalGMPForm(data,link) {
    return this.http.post<any>(environment.baseUrl + "/Inspection/LocalGMPInspection/"+link, data);
  }

  editLocalGMPForm(data, link){
    return this.http.put<any>(environment.baseUrl + "/Inspection/LocalGMPInspection/"+link, data);
  }

  getAbroadInspection(id):Observable<Inspection>{
	  return this.http.get<Inspection>(environment.baseUrl+'/Inspection/'+id);
  }

  public uploadFile(fileUploadModel: FileUploadModel): Observable<any> {
	  console.log("this.fileUploadModel",fileUploadModel);
    const formData: FormData = new FormData();
    Object.keys(fileUploadModel).forEach(key => {
      formData.append(key, fileUploadModel[key]);
    });
	console.log("formData : ",formData);
	return this.http.post(`${environment.baseUrl}/Attachment/Inspection`, formData);
  }


  downloadAttachment(documentInfo: any): void {
    const attachmentEndpoint = `${environment.baseUrl}/DocumentRef/GMP//${documentInfo.id}`;
    this.http.post(attachmentEndpoint, {}, {responseType: 'arraybuffer'}).subscribe((response) => {
      const file = new Blob([response], {
        type: documentInfo.fileType
      });
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = URL.createObjectURL(file);
      a.download = `${documentInfo.title}.${documentInfo.fileType.split('/')[1]}`;
      a.click();
    });
  }

  getInspectionHistory(id){
    return this.http.get<any>(this.inspectionUrl + '/GetHistory/' + id);
  }
  getInspectionProcessingTime(id){
     return this.http.get<any>(this.inspectionUrl + '/GetProcessingTime/' + id);
  } 
  
  getFacilities(){
	  return this.http.get<any>(`${environment.baseUrl}/Inspection/LocalGMPInspection/New/GetFacilities`);
  }

  getScreeningChecklists(inspectionTypeId: number, approvalMethodId: number){
    return this.http.get<ChecklistModel[]>(this.inspectionChecklistUrl + '/' + inspectionTypeId + '/' + approvalMethodId);
  }

  submitScreenerResponse(response){
    return this.http.post<boolean>(this.inspectionUrl + '/SubmitScreenerResponse', response);
  }

  getInspectionFees(){
    return this.http.get<any[]>(environment.baseUrl + '/InspectionFee');
  }

  //TODO: move this to CommonService
  // gets different kind of data; used with Formly Forms
  getData(url){
    return this.http.get<any>(url);
  }

  getAttachmentData(url, method, attachmentlist:AttachmentList){
    console.log(attachmentlist);
    if(method == 'get'){
      return this.http.get<any>(url);
    } else {
      return this.http.post<any>(url, attachmentlist);
    }
  }

  getInspectionListUrl(inspection : Inspection){
    switch(inspection.type.inspectionTypeCode){
      case InspectionTypes.POST_LICENSING_INSPECTION:
        return "/inspections/postLicensing/list/" + InspectionTypes.POST_LICENSING_INSPECTION;
      case InspectionTypes.NEW_LOCAL_GMP_INSPECTION:
      case InspectionTypes.LOCAL_GMP_RENEWAL_INSPECTION:
        return "/inspections/localGMP/list/LOCALGMP";
      case InspectionTypes.NEW_ABROAD_CGMP_INSPECTION: 
      case InspectionTypes.ABROAD_CGMP_RENEWAL_INSPECTION:
        return "/inspections/abroadCGMP/list/ABROADCGMP";
      case InspectionTypes.NEW_ABROAD_CGMP_INSPECTION_WAIVER:
      case InspectionTypes.ABROAD_CGMP_INSPECTION_WAIVER_RENEWAL:
        return "/inspections/abroadCGMP/list/ABROADCGMPWAIVER";
    }
  }

  addProductionLine(data: ProductionLine){
    return this.http.post<any>(environment.baseUrl + "/ProductCategory/AddProductionLine", data);
  }

  submitFurtherInformation(inspectionId, data) {
    return this.http.post<any>(environment.baseUrl + "/Inspection/SubmitFurtherInformation/" + inspectionId, data);
  }

  deleteQRCodeDocument(inspectionId){
    return this.http.delete<boolean>(SubDataVariables.POST_INSPECTION_QRCODE_URL + '/' + inspectionId);
  }

  activateDeactivateProductionLine(prodLineId: number) {
    return this.http.get<boolean>(environment.baseUrl + "/ProductCategory/ActivateDeactivate/" + prodLineId);
  }

  getAppliedFees(event : any, currentInspectionType : string,inspectionFees: InspectionFee[] ) : InspectionFee[] {
    let appliedFees : InspectionFee[] = [];
    if([InspectionTypes.NEW_ABROAD_CGMP_INSPECTION, InspectionTypes.ABROAD_CGMP_RENEWAL_INSPECTION].includes(currentInspectionType)) {
      // for normal abroad => payment category + multiple facility + >2 production line fees 
      if(event.paymentInformation && event.paymentInformation.inspectionFee){
        appliedFees.push(inspectionFees.find(f => f.id == event.paymentInformation.inspectionFee.id));                        
      }

      let checkBoxKeys = (<any>Object).entries(event.paymentInformation).map(m => m[0]).filter(key => ['WHRF', 'PFPQ', 'QCDL'].indexOf(key) != -1)
      for (const key of checkBoxKeys) {
        if(event.paymentInformation[key] == true){
          appliedFees.push(inspectionFees.find(f => f.code == key));
        }
      }

      let prodDetails = event.productionInformation.productDetailData?.productDetails;
      if(this.countProductionLinesForFee(prodDetails) > 2) {
        appliedFees.push(inspectionFees.find(f => f.code == 'MTTP'));
      }
    } else {
      appliedFees.push(inspectionFees.find(f => f.isSra == true));
    }
    
    let prodDetails = event.productionInformation.productDetailData?.productDetails;
    // multiple offsite facility payment
    if(prodDetails && prodDetails.length > 0) {
      const { uniqueOffsiteFacilitiesByNameAndAddressCount } = this.getOffsiteFacilitiesInfo(prodDetails)  

      if(uniqueOffsiteFacilitiesByNameAndAddressCount > 0) {
        appliedFees.push(inspectionFees.find(f => f.code == "MOF"));
      }   
    }

    return appliedFees;
  }

  regenerateIinspectDocumentAtCurrentStatus(inspectionId: number) {
    return this.http.get<boolean>(this.docGeneratorUrl + '/RegenerateIInspectDocument/' + inspectionId);
  }

  getOffsiteFacilitiesInfo(prodDetails: GmpProductDetail[] ) : { totalOffsiteFacilities : OffsiteFacility[] ; uniqueOffsiteFacilitiesByNameAndAddressCount: number } {
      const uniqueOffsiteFacilitiesMapByIdentifier = prodDetails.flatMap(pd => pd.offsiteFacilities).reduce((map, offsiteFacility) => {
        // Group offsiteFacilities by identifier
        map.set(offsiteFacility.identifier, offsiteFacility);
        return map;
    }, new Map<number, OffsiteFacility>());
    
    // Convert Map values to array to get unique offsite facilities
    let totalOffsiteFacilities = Array.from(uniqueOffsiteFacilitiesMapByIdentifier.values());
    totalOffsiteFacilities.forEach((offsiteFacility) => {
      offsiteFacility.productionLines = prodDetails.filter(s => s.offsiteFacilities.findIndex(x => x.identifier == offsiteFacility.identifier) > -1).map(p => p.productionLine);
    });

    let uniqueOffsiteFacilitiesByNameAndAddressCount = new Set(totalOffsiteFacilities.map(s => 
      `${s.name.trim().toLowerCase()}_${s.address.trim().toLowerCase()}_${s.streetNumber.trim().toLowerCase()}`)).size;

    return { totalOffsiteFacilities: totalOffsiteFacilities , uniqueOffsiteFacilitiesByNameAndAddressCount: uniqueOffsiteFacilitiesByNameAndAddressCount };
  }

  // Unique production-line IDs; grouped lookup lines count as one.
  countProductionLinesForFee(productDetails?: Array<{ productionLine?: ProductionLine }>): number {
    const unique = new Map<number, ProductionLine>();
    for (const detail of productDetails || []) {
      const line = detail?.productionLine;
      if (!line || line.id == null || line.isActive === false) {
        continue;
      }
      if (!unique.has(line.id)) {
        unique.set(line.id, line);
      }
    }
    const grouped = Array.from(unique.values()).filter(p => p.group != null);
    return unique.size - grouped.length + new Set(grouped.map(p => p.group)).size;
  }
}
